import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Typography } from "components/atoms";
import { TimerIcon, BellIcon, Spinner } from "components/atoms/Icon";
import { Modal } from "../Modal";
import { Timer } from "../Timer";
import { NotificationBody } from "../NotificationBody";
import { NotificationSection } from "../NotificationSection";
import { useScreenSize, useAppDispatch, useAppSelector } from "hooks";
import { handleGlobalModalClose } from "state/reducers/studyTimerSlice";
import { closeIframeModal } from "state/reducers/layoutSlice";
import { TimerSubmitPopUp } from "../TimerPlayPausePopUp";
import { MOBILE_WIDTH } from "utils/constants";
import {
  useGetAllNotificationsQuery,
  useGetUnreadNotificationsCountQuery,
  useMarkNotificationAsReadMutation,
} from "services/alerts";
import { FetchError } from "../FetchErrorMsg";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
// import updateLocale from "dayjs/plugin/updateLocale";
import "./index.scss";

dayjs.extend(relativeTime);
// dayjs.extend(updateLocale);

// dayjs.updateLocale("en", {
//   relativeTime: {
//     future: "in %s",
//     past: "%s",
//     s: "s",
//     m: "m",
//     mm: "%dm",
//     h: "hr",
//     hh: "%dhr",
//     d: "day",
//     dd: "%ddays",
//     M: "month",
//     MM: "%dmonths",
//     y: "ayear",
//     yy: "%dyears",
//   },
// });

interface IProps {
  theme: "light" | "dark";
  styles?: string;
  isAlert?: boolean;
}

export const HeaderNotification = ({ theme, isAlert }: IProps) => {
  const [isAlertsOpen, setIsAlertsOpen] = useState(false);
  const screen = useScreenSize();
  const navigate = useNavigate();
  const location: any = useLocation();
  const dispatch: any = useAppDispatch();
  const alertRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);

  const { hours, minutes, seconds, isModalOpen, isTimePause, isTimeSubmit } =
    useAppSelector(
      (state: {
        studyTimer: {
          hours: number;
          minutes: number;
          seconds: number;
          isTimeSubmit: boolean;
          isTimePause: boolean;
          isModalOpen: boolean;
        };
      }) => state.studyTimer
    );

  const {
    data,
    isLoading,
    isError,
    refetch: refetchGetAllNotifications,
  } = useGetAllNotificationsQuery();

  const {
    data: unreadData,
    isLoading: isUnreadLoading,
    isError: isUnreadError,
    refetch: refetchGetUnreadNotificationsCount,
  } = useGetUnreadNotificationsCountQuery();

  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();

  const handelModalClose = () => {
    setIsAlertsOpen(false);
  };

  const formatTime = (value: number) => {
    return value.toString().padStart(2, "0");
  };

  const handelTimerModalDefaultClose = () => {
    dispatch(handleGlobalModalClose(false));
  };

  const handelModalOpen = () => {
    dispatch(handleGlobalModalClose(true));
  };

  const handleViewAllClick = () => {
    setIsAlertsOpen(false);
    if (location.pathname.includes("self-paced")) {
      navigate("/self-paced/alert");
    } else {
      navigate("/alert");
    }
  };

  const handleAlertPopUpOpen = () => {
    if (screen.width <= MOBILE_WIDTH) {
      // Close score report modal when navigating to alert page in mobile
      dispatch(closeIframeModal());
      
      if (location.pathname.includes("self-paced")) {
        navigate("/self-paced/alert");
      } else {
        navigate("/alert");
      }
    } else {
      setIsAlertsOpen((prev) => !prev);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (alertRef.current && !alertRef.current.contains(event.target as Node)) {
      handelModalClose();
    }
  };

  const handleNotificationClick = async (item: any) => {
    window.open(item.LaunchUrl, "__blank");
    try {
      await markNotificationAsRead({
        notificationGuid: item.NotificationGuid,
      }).then(() => {
        refetchGetAllNotifications();
        refetchGetUnreadNotificationsCount();
      });
    } catch (err: any) {
      console.log(err);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (screen.width <= MOBILE_WIDTH) {
      setIsAlertsOpen(false);
    }
  }, [screen.width, navigate]);

  return (
    <>
      <div className="header-notify-section-wrapper">
        <div className="timer-icon">
          <TimerIcon
            color={
              screen.width < 1024
                ? "white"
                : theme === "light"
                  ? "black"
                  : "white"
            }
            isSubmit={isTimeSubmit}
            onClick={handelModalOpen}
          />

          {isTimeSubmit && (
            <TimerSubmitPopUp
              theme={theme}
              hours={hours}
              minutes={minutes}
              seconds={seconds}
              isTimePause={isTimePause}
              isTimeSubmit
            />
          )}
        </div>

        {isAlert ? (
          <div className="alert-notification">
            <BellIcon color={"white"} />
          </div>
        ) : (
          <div className="bell-icon" ref={bellRef}>
            {isUnreadLoading ? (
              <Spinner theme={theme} />
            ) : isUnreadError ? (
              <FetchError content="Oops! failed to load resources." />
            ) : unreadData &&
              Array.isArray(unreadData) &&
              unreadData.length === 0 ? (
              <Typography label="No data available" variant="h5Med" />
            ) : (
              <NotificationSection
                theme={theme}
                count={unreadData}
                onClick={handleAlertPopUpOpen}
              />
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handelTimerModalDefaultClose}
        style="timer-modal-custom-style"
        theme={theme}
      >
        <Timer theme={theme} />
      </Modal>

      <Modal
        isOpen={isAlertsOpen}
        onClose={handelModalClose}
        style="notification-modal-custom-style"
        referenceElement={bellRef}
        isEnableDynamicPositionCss={true}
        theme={theme}
      >
        <div className="pop-up" ref={alertRef}>
          <div className="header">
            <Typography label="Alerts" variant="h9Bold" styles="left-section" />
            {screen.width > 592 ? (
              <Typography
                label="View All"
                variant="h9Medium"
                styles="right-section"
                onClick={handleViewAllClick}
              />
            ) : null}
          </div>
          <div className="body">
            {isLoading ? (
              <Spinner theme={theme} />
            ) : isError ? (
              <FetchError content="Oops! failed to load resources." />
            ) : data && Array.isArray(data) && data.filter((item: any) => item.Filter !== "Discounts").length === 0 ? (
              <div className="empty-notification-wrapper">
                <Typography
                  label="No notifications available"
                  variant="h8Med"
                />
              </div>
            ) : (
              <>
                {data.filter((item: any) => item.Filter !== "Discounts").map((item: any, index: number) => (
                  <NotificationBody
                    key={index}
                    title={item.Title}
                    text={item.Body}
                    labels={item.Labels}
                    isRead={Boolean(item.IsRead)}
                    time={dayjs(item.SendDateUtc).fromNow()}
                    onClick={() => handleNotificationClick(item)}
                  />
                ))}
              </>
            )}
          </div>
          <div className="discount-header">
            <Typography label="Important Discount Reminders" variant="h9Bold" styles="left-section" />
          </div>
          <div className="body">
            {isLoading ? (
              <Spinner theme={theme} />
            ) : isError ? (
              <FetchError content="Oops! failed to load resources." />
            ) : data && Array.isArray(data) && data.filter((item: any) => item.Filter === "Discounts").length === 0 ? (
              <div className="empty-notification-wrapper">
                <Typography
                  label="No notifications available"
                  variant="h8Med"
                />
              </div>
            ) : (
              <>
                {data.filter((item: any) => item.Filter === "Discounts").map((item: any, index: number) => (
                  <NotificationBody
                    key={index}
                    title={item.Title}
                    text={item.Body}
                    labels={item.Labels}
                    isRead={Boolean(item.IsRead)}
                    time={dayjs(item.SendDateUtc).fromNow()}
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};
